import { useEffect, useState, useCallback } from "react";
import { SaudiRiyal } from "./SaudiRiyal";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis, LabelList, Legend,
  ComposedChart, Line, CartesianGrid,
} from "recharts";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import {
  Fuel, Droplets, Package, Gauge, TrendingUp, TrendingDown, AlertTriangle,
  Download, Loader2, Building2, Users, CircleDollarSign, ShieldCheck,
} from "lucide-react";
import { api } from "../lib/api";

const monthLabel = (mk) => {
  if (!mk) return "";
  const [y, m] = mk.split("-");
  const names = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
  return `${names[(parseInt(m, 10) || 1) - 1]} ${y}`;
};
const nfmt = (n) => Number(n || 0).toLocaleString("en-US");

const Kpi = ({ icon: Icon, label, value, unit, grad, testid }) => (
  <div data-testid={testid} className={`relative overflow-hidden rounded-2xl p-4 text-white shadow-lg bg-gradient-to-br ${grad}`}>
    <Icon className="pointer-events-none absolute -bottom-3 -left-3 h-16 w-16 text-white/15" />
    <p className="text-[11px] font-semibold text-white/85">{label}</p>
    <p className="mt-1 font-heading text-3xl font-extrabold font-mono-nums leading-none drop-shadow-sm" dir="ltr">
      {value}{unit ? <span className="text-sm font-bold text-white/80"> {unit}</span> : null}
    </p>
  </div>
);

const ChartCard = ({ title, icon: Icon, children, accent = "text-amber-600", testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <p className="font-heading font-bold text-slate-800 mb-3 flex items-center gap-2">
      <Icon className={`h-4 w-4 ${accent}`} /> {title}
    </p>
    {children}
  </div>
);

// Semi-circle gauge (0-100)
const HealthGauge = ({ pct }) => {
  const val = Math.max(0, Math.min(100, Math.round(pct || 0)));
  const color = val >= 70 ? "#10b981" : val >= 40 ? "#f59e0b" : "#ef4444";
  const gData = [{ name: "v", value: val }, { name: "r", value: 100 - val }];
  return (
    <div className="relative" dir="ltr">
      <ResponsiveContainer width="100%" height={170}>
        <PieChart>
          <Pie data={gData} dataKey="value" startAngle={180} endAngle={0} cx="50%" cy="90%"
            innerRadius={72} outerRadius={104} stroke="none" isAnimationActive>
            <Cell fill={color} />
            <Cell fill="#eef2f6" />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-x-0 bottom-2 flex flex-col items-center">
        <span className="font-heading text-4xl font-extrabold font-mono-nums" style={{ color }}>{val}%</span>
        <span className="text-[11px] text-slate-400">نسبة المناديب الأكفاء</span>
      </div>
    </div>
  );
};

const FLAG = {
  ok: { lbl: "كفؤ", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  over: { lbl: "مرتفع الاستهلاك", cls: "bg-red-50 text-red-700 border-red-200" },
  under: { lbl: "منخفض لافت", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  nodata: { lbl: "بدون بيانات وقود", cls: "bg-slate-50 text-slate-500 border-slate-200" },
};
const NAME_TO_FLAG = Object.fromEntries(Object.entries(FLAG).map(([k, v]) => [v.lbl, k]));

const GRID_COLS = "grid items-center grid-cols-[1.7fr_1.2fr_1fr_0.85fr_0.85fr_0.85fr_0.85fr_1fr_1.2fr]";

export const FuelReportPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState("");
  const [branch, setBranch] = useState("all");
  const [vtype, setVtype] = useState("all");
  const [downloading, setDownloading] = useState(false);
  const [mode, setMode] = useState("day");
  const [day, setDay] = useState("");
  const [trend, setTrend] = useState(null);
  const [trendMonths, setTrendMonths] = useState("6");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.fuelReport(month, branch, vtype, mode === "day" ? day : "");
      if (mode === "day" && !day && (d.days || []).length) {
        const withFuel = (d.days || []).filter((x) => (d.fuel_days || []).includes(x));
        setMonth(d.month);
        setDay((withFuel.length ? withFuel : d.days).slice(-1)[0]);
        return;
      }
      setData(d);
      if (!month && d.month) setMonth(d.month);
    } catch (e) {
      toast.error("تعذّر تحميل تقرير استهلاك الوقود");
    } finally {
      setLoading(false);
    }
  }, [month, branch, vtype, mode, day]);

  useEffect(() => { load(); }, [load]);

  const loadTrend = useCallback(async () => {
    try {
      setTrend(await api.fuelReportTrend(parseInt(trendMonths, 10), branch, vtype));
    } catch (e) { /* silent */ }
  }, [trendMonths, branch, vtype]);

  useEffect(() => { loadTrend(); }, [loadTrend]);

  const doExport = async () => {
    setDownloading(true);
    try {
      const url = api.fuelReportExportUrl(month || data?.month, branch, vtype, mode === "day" ? day : "");
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `fuel_report_${(mode === "day" && day) || month || data?.month}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("تم تنزيل التقرير");
    } catch (e) {
      toast.error("تعذّر تصدير الملف");
    } finally { setDownloading(false); }
  };

  const s = data?.summary || {};
  const statusData = (data?.by_status || []).filter((d) => d.value > 0);
  const [effCat, setEffCat] = useState(null);
  const branchBar = (data?.by_branch || []).map((b) => ({ name: b.branch, liters: b.liters, expected: 0, orders: b.orders }));
  const vehicleBar = (data?.by_vehicle || []).map((v) => ({ name: v.type, liters: v.liters, orders: v.orders, count: v.count }));
  const topRiders = (data?.rows || []).filter((r) => r.has_fuel).slice(0, 8)
    .map((r) => ({ name: (r.name || "").split(" ").slice(0, 2).join(" "), "الفعلي": r.liters, "المتوقع": r.expected_liters }));
  const fleetHealth = (s.efficient + s.over + s.under) > 0 ? (s.efficient / (s.efficient + s.over + s.under)) * 100 : 0;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="space-y-5" data-testid="fuel-report-panel" dir="rtl">
      {/* Header + filters */}
      <div className="rounded-2xl border border-amber-200 bg-gradient-to-l from-amber-50 to-white p-4">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="h-10 w-10 rounded-xl bg-amber-500/15 flex items-center justify-center"><Fuel className="h-5 w-5 text-amber-600" /></div>
          <div>
            <div className="font-heading font-extrabold text-slate-900">تقرير استهلاك الوقود الشامل</div>
            <p className="text-xs text-slate-500">تحليل بياني لكفاءة استهلاك الوقود لكل المناديب مقارنةً بالمعيار.</p>
          </div>
        </div>
        <div className="mb-2 flex flex-wrap items-center gap-2" data-testid="fuel-report-mode">
          {[["day", "تقرير يومي"], ["month", "تقرير شهري"]].map(([k, l]) => (
            <button key={k} type="button" data-testid={`fuel-mode-${k}`} onClick={() => { setMode(k); setDay(""); }}
              className={`rounded-full border px-4 py-1.5 text-xs font-bold transition-colors ${mode === k ? "border-amber-600 bg-amber-600 text-white" : "border-amber-200 bg-white text-amber-800 hover:bg-amber-50"}`}>{l}</button>
          ))}
          {mode === "day" && data && (
            <Select value={day} onValueChange={setDay}>
              <SelectTrigger data-testid="fuel-day-select" className="h-9 w-56 bg-white"><SelectValue placeholder="اختر اليوم" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {[...(data.days || [])].reverse().map((d) => (
                  <SelectItem key={d} value={d}>{d}{(data.fuel_days || []).includes(d) ? " ⛽ فيه وقود" : " — بدون وقود"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <Select value={month} onValueChange={(m) => { setMonth(m); setDay(""); }}>
            <SelectTrigger data-testid="fuel-month-select" className="bg-white h-11"><SelectValue placeholder="الشهر" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {(data?.available_months || []).map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={branch} onValueChange={setBranch}>
            <SelectTrigger data-testid="fuel-branch-select" className="bg-white h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">كل الفروع</SelectItem>
              {(data?.branches || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={vtype} onValueChange={setVtype}>
            <SelectTrigger data-testid="fuel-vehicle-select" className="bg-white h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">كل المركبات</SelectItem>
              {(data?.vehicle_types || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="fuel-export-btn" onClick={doExport} disabled={downloading || loading}
            className="h-11 bg-amber-600 hover:bg-amber-700 gap-1.5">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} تصدير Excel
          </Button>
        </div>
      </div>

      {loading || !data ? (
        <div className="py-16 text-center text-slate-400">جارٍ تحميل التقرير…</div>
      ) : (
        <>
          <div data-testid="fuel-period-info" className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs text-slate-600">
            {data.day
              ? <span>تقرير يوم <b className="text-blue-700" dir="ltr">{data.day}</b>: طلبات هذا اليوم فقط مقابل الوقود المعبأ في نفس اليوم.</span>
              : mode === "day" ? <span className="text-amber-700">لا توجد أيام مرفوعة في هذا الشهر — يُعرض الشهر كاملاً.</span>
              : <span>الطلبات محسوبة لـ <b className="text-slate-800">{monthLabel(data.month)}</b> كاملاً:</span>}
            {data.day && data.undated_fuel_records > 0 && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-700">{data.undated_fuel_records} عملية وقود في ملف شهري بدون تاريخ يومي — لا تظهر في التقرير اليومي</span>}
            {!data.day && (data.period?.days ? <span>من <b dir="ltr">{data.period.from}</b> إلى <b dir="ltr">{data.period.to}</b> · <b className="text-blue-700">{data.period.days} يوم</b> من تقارير المنصة</span> : <span>لا توجد تقارير طلبات</span>)}
            {data.estimated_liters_records > 0 && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-700">{data.estimated_liters_records} عملية بدون لترات — قُدّرت من المبلغ ÷ سعر اللتر</span>}
          </div>
          {data.month_fuel_records === 0 && (
            <div data-testid="fuel-no-fuel-warning" className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>{data.day ? <>لا توجد تعبئة وقود مسجلة يوم <b dir="ltr">{data.day}</b>{(data.fuel_days || []).length ? " — اختر يوماً عليه علامة ⛽ من القائمة أعلاه." : "، ولا توجد تعبئات بتاريخ يومي في هذا الشهر."}</> : <>لا يوجد ملف وقود مرفوع لشهر <b>{monthLabel(data.month)}</b>، لذلك لا توجد لترات فعلية للمقارنة.</>}
                {!data.day && ((data.fuel_months || []).length > 0
                  ? <> الأشهر التي فيها وقود: {data.fuel_months.slice(0, 4).map((m) => <button key={m} type="button" data-testid={`fuel-goto-${m}`} onClick={() => { setMonth(m); setDay(""); }} className="mx-1 rounded bg-white px-1.5 py-0.5 font-bold text-amber-900 underline-offset-2 hover:underline">{monthLabel(m)}</button>)}</>
                  : " ارفع ملف الوقود من تبويب «الوقود» أولاً.")}
              </div>
            </div>
          )}
          {data.month_fuel_records > 0 && data.unlinked_fuel_liters > 0 && (
            <p data-testid="fuel-unlinked-note" className="rounded-xl bg-slate-50 px-4 py-2 text-xs text-slate-500">{nfmt(data.unlinked_fuel_liters)} لتر في ملف الوقود لم تُربط بأي مندوب في التقرير (لوحة غير مسجلة على مندوب) — أو لمندوب ليس له طلبات هذا الشهر.</p>
          )}
          {/* KPI hero */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            <Kpi testid="fuel-kpi-riders" icon={Users} label="عدد المناديب" value={nfmt(s.riders)} grad="from-slate-600 to-slate-800" />
            <Kpi testid="fuel-kpi-orders" icon={Package} label="إجمالي الطلبات" value={nfmt(s.orders)} grad="from-blue-500 to-blue-700" />
            <Kpi testid="fuel-kpi-liters" icon={Droplets} label="إجمالي اللترات" value={nfmt(s.liters)} unit="ل" grad="from-amber-500 to-orange-600" />
            <Kpi testid="fuel-kpi-cost" icon={CircleDollarSign} label="تكلفة الوقود" value={nfmt(s.fuel_sar)} unit={<SaudiRiyal />} grad="from-emerald-500 to-teal-600" />
            <Kpi testid="fuel-kpi-opl" icon={Gauge} label="متوسط الكفاءة" value={s.avg_opl} unit="طلب/ل" grad="from-violet-500 to-purple-700" />
            <Kpi testid="fuel-kpi-dev" icon={s.overall_dev > 0 ? TrendingUp : TrendingDown} label="الانحراف الكلي" value={`${s.overall_dev > 0 ? "+" : ""}${s.overall_dev}`} unit="%" grad={s.overall_dev > (data.threshold) ? "from-rose-500 to-red-700" : "from-cyan-500 to-blue-700"} />
          </div>

          {/* Month-over-month comparison */}
          <div data-testid="fuel-trend-card" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
              <p className="font-heading font-bold text-slate-800 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-blue-600" /> مقارنة استهلاك الوقود بين الأشهر
              </p>
              <Select value={trendMonths} onValueChange={setTrendMonths}>
                <SelectTrigger data-testid="fuel-trend-months" className="w-36 h-9 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">آخر ٣ أشهر</SelectItem>
                  <SelectItem value="6">آخر ٦ أشهر</SelectItem>
                  <SelectItem value="12">آخر ١٢ شهر</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!trend || (trend.series || []).length === 0 ? (
              <p className="text-sm text-slate-400 py-8 text-center">لا توجد بيانات كافية للمقارنة</p>
            ) : (
              <div dir="ltr">
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={(trend.series || []).map((s) => ({ ...s, m: monthLabel(s.month) }))} margin={{ left: -10, right: 10, top: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
                    <XAxis dataKey="m" tick={{ fontSize: 11, fill: "#475569" }} />
                    <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                    <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                    <Tooltip formatter={(v, n) => {
                      const map = { "الفعلي": "لتر", "المتوقع": "لتر", "الكفاءة": "طلب/ل", "التكلفة": "⃁" };
                      return [`${nfmt(v)} ${map[n] || ""}`.trim(), n];
                    }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="l" dataKey="liters" name="الفعلي" fill="#f97316" radius={[5, 5, 0, 0]} barSize={22} />
                    <Bar yAxisId="l" dataKey="expected_liters" name="المتوقع" fill="#cbd5e1" radius={[5, 5, 0, 0]} barSize={22} />
                    <Line yAxisId="r" type="monotone" dataKey="avg_opl" name="الكفاءة" stroke="#7c3aed" strokeWidth={3} dot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
                <p className="mt-1 text-center text-[11px] text-slate-400">الأعمدة: اللترات الفعلية مقابل المتوقعة · الخط البنفسجي: متوسط الكفاءة (طلب/لتر)</p>
              </div>
            )}
          </div>

          {/* Status donut + gauge */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <ChartCard testid="fuel-status-donut" title="توزيع كفاءة المناديب" icon={ShieldCheck} accent="text-emerald-600">
              {statusData.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">لا توجد بيانات</p> : (
                <div className="flex items-center gap-3" dir="ltr">
                  <ResponsiveContainer width="55%" height={190}>
                    <PieChart>
                      <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={2}
                        onClick={(e) => e && e.name && setEffCat(NAME_TO_FLAG[e.name])} className="cursor-pointer">
                        {statusData.map((d, i) => <Cell key={i} fill={d.color} className="cursor-pointer" />)}
                      </Pie>
                      <Tooltip formatter={(v, n) => [`${v} مندوب`, n]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-2" dir="rtl">
                    {statusData.map((d) => (
                      <button key={d.name} type="button" data-testid={`eff-legend-${NAME_TO_FLAG[d.name] || d.name}`}
                        onClick={() => setEffCat(NAME_TO_FLAG[d.name])}
                        className="flex w-full items-center justify-between rounded-lg px-1.5 py-1 text-sm transition-colors hover:bg-slate-50">
                        <span className="flex items-center gap-2 text-slate-600"><span className="h-3 w-3 rounded-full" style={{ background: d.color }} /> {d.name}</span>
                        <span className="font-mono-nums font-bold text-slate-800">{d.value} ›</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </ChartCard>

            {effCat && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="eff-detail-modal" onClick={() => setEffCat(null)}>
                <div className="flex max-h-[80vh] w-full max-w-md flex-col rounded-2xl bg-white p-4 shadow-2xl" dir="rtl" onClick={(e) => e.stopPropagation()}>
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="font-heading font-bold text-slate-800">
                      {FLAG[effCat]?.lbl} · {(data?.rows || []).filter((r) => r.flag === effCat).length} مندوب
                    </h3>
                    <button data-testid="eff-detail-close" onClick={() => setEffCat(null)} className="text-slate-400 hover:text-slate-700">✕</button>
                  </div>
                  <div className="divide-y divide-slate-100 overflow-y-auto">
                    {(data?.rows || []).filter((r) => r.flag === effCat).map((r) => (
                      <div key={r.rider_id} className="flex items-center justify-between gap-2 py-2 text-sm">
                        <div className="min-w-0">
                          <div className="truncate font-semibold text-slate-800">{r.name}</div>
                          <div className="truncate text-xs text-slate-400">{r.branch} · {r.vehicle_label}</div>
                        </div>
                        <div className="shrink-0 text-left" dir="ltr">
                          <div className="font-mono-nums text-slate-700">{nfmt(r.liters)} لتر</div>
                          <div className="text-xs text-slate-400">{r.has_fuel ? `${r.dev > 0 ? "+" : ""}${r.dev}%` : "—"}</div>
                        </div>
                      </div>
                    ))}
                    {(data?.rows || []).filter((r) => r.flag === effCat).length === 0 && (
                      <p className="py-8 text-center text-sm text-slate-400">لا يوجد مناديب في هذه الفئة</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <ChartCard testid="fuel-gauge" title="صحة أسطول الوقود" icon={Gauge} accent="text-violet-600">
              <HealthGauge pct={fleetHealth} />
              <div className="mt-1 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-emerald-50 py-1.5"><p className="font-mono-nums font-bold text-emerald-700">{s.efficient}</p><p className="text-[10px] text-slate-500">كفؤ</p></div>
                <div className="rounded-lg bg-red-50 py-1.5"><p className="font-mono-nums font-bold text-red-700">{s.over}</p><p className="text-[10px] text-slate-500">مرتفع</p></div>
                <div className="rounded-lg bg-amber-50 py-1.5"><p className="font-mono-nums font-bold text-amber-700">{s.under}</p><p className="text-[10px] text-slate-500">منخفض</p></div>
              </div>
            </ChartCard>

            <ChartCard testid="fuel-vehicle-bar" title="اللترات حسب نوع المركبة" icon={Fuel} accent="text-orange-600">
              {vehicleBar.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">لا توجد بيانات</p> : (
                <div dir="ltr">
                  <ResponsiveContainer width="100%" height={190}>
                    <BarChart data={vehicleBar} margin={{ left: -10, right: 10, top: 10 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} />
                      <Tooltip formatter={(v, n) => [`${nfmt(v)} ${n === "liters" ? "ل" : "طلب"}`, n === "liters" ? "لترات" : "طلبات"]} cursor={{ fill: "rgba(249,115,22,0.08)" }} />
                      <Bar dataKey="liters" fill="#f97316" radius={[6, 6, 0, 0]}>
                        <LabelList dataKey="liters" position="top" style={{ fontSize: 11, fill: "#c2410c", fontWeight: 700 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </ChartCard>
          </div>

          {/* Branch bar + top riders bar */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard testid="fuel-branch-bar" title="استهلاك اللترات حسب الفرع" icon={Building2} accent="text-blue-600">
              {branchBar.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">لا توجد بيانات</p> : (
                <div dir="ltr">
                  <ResponsiveContainer width="100%" height={Math.max(200, branchBar.length * 40)}>
                    <BarChart data={branchBar} layout="vertical" margin={{ left: 10, right: 40 }}>
                      <XAxis type="number" hide />
                      <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 12, fill: "#475569" }} />
                      <Tooltip formatter={(v) => [`${nfmt(v)} ل`, "لترات"]} cursor={{ fill: "rgba(37,99,235,0.08)" }} />
                      <Bar dataKey="liters" fill="#2563eb" radius={[0, 6, 6, 0]}>
                        <LabelList dataKey="liters" position="right" style={{ fontSize: 11, fill: "#1e40af", fontWeight: 700 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </ChartCard>

            <ChartCard testid="fuel-top-bar" title="أعلى المناديب: الفعلي مقابل المتوقع" icon={Droplets} accent="text-amber-600">
              {topRiders.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center">لا توجد بيانات وقود</p> : (
                <div dir="ltr">
                  <ResponsiveContainer width="100%" height={Math.max(200, topRiders.length * 38)}>
                    <BarChart data={topRiders} layout="vertical" margin={{ left: 10, right: 20 }} barGap={2}>
                      <XAxis type="number" hide />
                      <YAxis type="category" dataKey="name" width={95} tick={{ fontSize: 11, fill: "#475569" }} />
                      <Tooltip formatter={(v, n) => [`${nfmt(v)} ل`, n]} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="الفعلي" fill="#f59e0b" radius={[0, 5, 5, 0]} />
                      <Bar dataKey="المتوقع" fill="#94a3b8" radius={[0, 5, 5, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </ChartCard>
          </div>

          {/* Alerts */}
          {(data.alerts || []).length > 0 && (
            <div data-testid="fuel-alerts" className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
              <p className="font-heading font-bold text-amber-800 mb-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" /> تنبيهات استهلاك الوقود ({data.alerts.length}) <span className="text-xs font-normal text-amber-600">حد التنبيه ±{data.threshold}%</span>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {data.alerts.map((r) => (
                  <div key={r.rider_id} data-testid={`fuel-alert-${r.rider_id}`}
                    className={`flex items-center justify-between rounded-xl border bg-white px-3 py-2 text-sm ${r.flag === "over" ? "border-red-200" : "border-amber-200"}`}>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{r.name}</p>
                      <p className="text-[11px] text-slate-500">{r.branch} · {r.vehicle_label} · فعلي {nfmt(r.liters)}ل / متوقع {nfmt(r.expected_liters)}ل</p>
                    </div>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold font-mono-nums ${r.flag === "over" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`} dir="ltr">
                      {r.dev > 0 ? "+" : ""}{r.dev}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Detailed table */}
          <div data-testid="fuel-table" className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="text-sm font-heading font-bold text-slate-800 px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <Package className="h-4 w-4 text-slate-500" /> تفاصيل المناديب ({(data.rows || []).length})
            </div>
            <div data-testid="fuel-table-legend" className="grid gap-1 border-b border-slate-100 bg-slate-50/50 px-4 py-2 text-[11px] text-slate-500 sm:grid-cols-2">
              <span><b className="text-slate-700">الطلبات:</b> {data.day ? `طلبات المندوب يوم ${data.day} فقط.` : `مجموع طلبات المندوب في كل أيام الشهر المرفوعة (${data.period?.days || 0} يوم).`}</span>
              <span><b className="text-slate-700">فعلي (ل):</b> اللترات المعبأة فعلاً من ملف الوقود للشهر نفسه.</span>
              <span><b className="text-slate-700">متوقع (ل):</b> اللترات التي يُفترض أن يستهلكها = الطلبات ÷ معيار المركبة (مثلاً بوكسر 1.5 طلب/لتر ← 44 طلب = 29.3 لتر).</span>
              <span><b className="text-slate-700">الانحراف:</b> الفرق بين الفعلي والمتوقع؛ أعلى من +{data.threshold}% = هدر، أقل من −{data.threshold}% = لافت.</span>
            </div>
            <div className="overflow-x-auto">
              <div className="min-w-[820px]">
                <div className={`${GRID_COLS} bg-slate-50 text-slate-500 text-xs`}>
                  <div className="px-3 py-2 font-semibold text-right">المندوب</div>
                  <div className="px-3 py-2 font-semibold text-right">الفرع</div>
                  <div className="px-3 py-2 font-semibold text-right">المركبة</div>
                  <div className="px-2 py-2 font-semibold text-center">الطلبات</div>
                  <div className="px-2 py-2 font-semibold text-center">فعلي (ل)</div>
                  <div className="px-2 py-2 font-semibold text-center">متوقع (ل)</div>
                  <div className="px-2 py-2 font-semibold text-center">طلب/لتر</div>
                  <div className="px-2 py-2 font-semibold text-center">الانحراف</div>
                  <div className="px-2 py-2 font-semibold text-center">الحالة</div>
                </div>
                {(data.rows || []).length === 0 ? (
                  <div className="px-3 py-8 text-center text-slate-400">لا توجد بيانات لهذا الشهر</div>
                ) : (data.rows || []).map((r) => {
                  const fl = FLAG[r.flag] || FLAG.nodata;
                  return (
                    <div key={r.rider_id} data-testid={`fuel-row-${r.rider_id}`} className={`${GRID_COLS} border-t border-slate-100 hover:bg-slate-50/60 text-sm`}>
                      <div className="px-3 py-2 font-semibold text-slate-800 text-right truncate">{r.name}</div>
                      <div className="px-3 py-2 text-slate-600 text-right truncate">{r.branch}</div>
                      <div className="px-3 py-2 text-slate-600 text-right truncate">{r.vehicle_label}</div>
                      <div className="px-2 py-2 font-mono-nums text-slate-700 text-center" dir="ltr">{nfmt(r.orders)}</div>
                      <div className={`px-2 py-2 font-mono-nums text-center ${r.has_fuel ? "text-slate-700 font-semibold" : "text-slate-300"}`} dir="ltr">{r.has_fuel ? nfmt(r.liters) : "—"}</div>
                      <div className="px-2 py-2 font-mono-nums text-slate-400 text-center" dir="ltr" title={`${nfmt(r.orders)} طلب ÷ ${r.benchmark_opl} طلب/لتر`}>{nfmt(r.expected_liters)}</div>
                      <div className="px-2 py-2 font-mono-nums font-bold text-slate-800 text-center" dir="ltr">{r.orders_per_liter || "—"}</div>
                      <div className={`px-2 py-2 font-mono-nums font-semibold text-center ${r.flag === "over" ? "text-red-600" : r.flag === "under" ? "text-amber-600" : "text-slate-500"}`} dir="ltr">
                        {r.has_fuel ? `${r.dev > 0 ? "+" : ""}${r.dev}%` : "—"}
                      </div>
                      <div className="px-2 py-2 text-center">
                        <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${fl.cls}`}>{fl.lbl}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
};
